import React, { useMemo, useState, useEffect } from 'react';
import styles from './landing-pricing.module.scss';
import { DefaultButton } from 'shared/ui';
import { CheckMarkSvg } from 'shared/svg';
import { useNavigate } from 'react-router-dom';
import { AppLinks } from 'app/router/consts/app-links.enum';
import { licenseCalcAmount } from 'features/license-calc/model/license-calc-amount';
import { LicenseEnum } from 'shared/types';
import type { IGetLicenseInfoProps } from 'shared/api/business/interfaces/get-license-info.props.interface';
import { BusinessApi } from 'shared/api';

const FEATURES = [
  'Безлимит записей',
  'Уведомления в VK',
  'Онлайн-запись на все площадки',
  'Аналитика и отчеты',
  'Приложение для клиентов',
  'Программы лояльности',
];

function formatPrice(n: number): string {
  return Math.round(n).toLocaleString('ru-RU');
}

function getPeriodLabel(months: number): string {
  if (months === 1) return `${months} месяц`;
  if (months >= 2 && months <= 4) return `${months} месяца`;
  return `${months} месяцев`;
}

export const LandingPricing: React.FC = () => {
  const [selectedEmployees, setSelectedEmployees] = useState(3);
  const [license, setLicense] = useState<IGetLicenseInfoProps | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    BusinessApi.getOwnerLicenseInfo()
      .then((data) => {
        setLicense(data);
      })
      .catch(() => undefined)
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleSignUp = () => navigate(AppLinks.SignUpLink);

  const isEnterprise = selectedEmployees > 20;

  const employeeNumbers = useMemo(() => {
    const nums: number[] = [];
    for (let i = 2; i <= 16; i++) nums.push(i);
    return nums;
  }, []);

  const DURATION_MONTHS = [1, 3, 6, 12];
  const durations = useMemo(() => {
    if (!license) return [];
    return DURATION_MONTHS.map((m) => ({
      months: m,
      period: getPeriodLabel(m),
      badge: m === 12 ? 'Самый выгодный' : '',
    }));
  }, [license]);

  const calc = license
    ? licenseCalcAmount(LicenseEnum.BASE, license, 0, selectedEmployees, 1, 0)
    : null;

  return (
    <div className={styles.pricing_outer} id="landing-pricing">
      <div className={styles.landing_pricing}>
        <h2 className={styles.landing_pricing__title}>Тарифы</h2>

        <div className={styles.landing_pricing__free_banner}>
          <div className={styles.banner__text}>
            <div className={styles.banner__title}>
              30 дней бесплатно для 2 сотрудников
            </div>
            <div className={styles.banner__subtitle}>
              Все функции без ограничений. Без привязки карты.
            </div>
          </div>
          <div className={styles.banner__cta}>
            <DefaultButton
              buttonText="Начать бесплатно"
              color="white"
              onClick={handleSignUp}
            />
          </div>
        </div>

        <div className={styles.landing_pricing__subtitle}>
          Платите только за сотрудников, оказывающих услуги
        </div>

        <div className={styles.landing_pricing__manager_note}>
          <CheckMarkSvg />
          Менеджеры и администраторы — бесплатно, без ограничений
        </div>

        <div className={styles.landing_pricing__selector}>
          <div className={styles.selector__label}>Сотрудников</div>
          <div className={styles.selector__grid}>
            {employeeNumbers.map((num) => (
              <button
                key={num}
                className={`${styles.selector__btn} ${selectedEmployees === num ? styles['selector__btn--active'] : ''}`}
                onClick={() => setSelectedEmployees(num)}
              >
                {num}
              </button>
            ))}
          </div>
        </div>

        {calc && (
          <div className={styles.landing_pricing__per_emp}>
            {formatPrice(calc.monthlyEmployeePrice)} /сотр. в месяц
          </div>
        )}

        {isEnterprise ? (
          <div className={styles.pricing_enterprise}>
            <div className={styles.enterprise__title}>От 20 сотрудников?</div>
            <div className={styles.enterprise__text}>
              Индивидуальные условия по запросу
            </div>
            <div className={styles.enterprise__cta}>
              <DefaultButton
                buttonText="Связаться с нами"
                color="green"
                onClick={handleSignUp}
              />
            </div>
          </div>
        ) : loading ? (
          <div className={styles.pricing_loading}>Загрузка...</div>
        ) : !license ? (
          <div className={styles.pricing_empty}>
            <div className={styles.pricing_empty_text}>
              Цены не загрузились. Повторите позже.
            </div>
          </div>
        ) : (
          <>
            <div className={styles.landing_pricing__columns}>
              {durations.map((duration) => {
                const result = licenseCalcAmount(
                  LicenseEnum.BASE,
                  license,
                  0,
                  selectedEmployees,
                  duration.months,
                  0
                );
                return (
                  <div
                    key={duration.months}
                    className={`${styles.pricing_card} ${duration.badge ? styles['pricing_card--highlighted'] : ''}`}
                  >
                    {duration.badge && (
                      <div className={styles.card__badge}>{duration.badge}</div>
                    )}
                    <div className={styles.card__period}>{duration.period}</div>
                    {result.totalDiscount > 0 && (
                      <div className={styles.card__discount}>
                        Скидка {result.totalDiscount}%
                      </div>
                    )}
                    <div className={styles.card__price}>
                      <span className={styles.price__value}>
                        {formatPrice(
                          result.monthlyTotalDiscPrice ||
                            result.monthlyTotalPrice
                        )}
                      </span>
                      <span className={styles.price__suffix}>/мес</span>
                    </div>
                    {result.totalDiscount > 0 && (
                      <div className={styles.card__old_price}>
                        {formatPrice(result.monthlyTotalPrice)} /мес
                      </div>
                    )}
                    <div className={styles.card__total}>
                      {formatPrice(result.totalPrice)} за{' '}
                      {duration.period.toLowerCase()}
                    </div>
                    <div className={styles.card__cta}>
                      <DefaultButton
                        buttonText="Попробовать бесплатно"
                        color={duration.badge ? 'green' : 'white'}
                        onClick={handleSignUp}
                      />
                    </div>
                    <div className={styles.card__trial}>30 дней бесплатно</div>
                  </div>
                );
              })}
            </div>
            <div className={styles.pricing_features}>
              <div className={styles.features__title}>Что входит</div>
              <div className={styles.features__grid}>
                {FEATURES.map((feature) => (
                  <div key={feature} className={styles.features__item}>
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
